import { format } from "date-fns";
import { fetchLeaderboardData } from "@/api/leaderboard";

/**
 * Where to land after signing in or signing up (owner, 3 Nov 2026): people who arrive
 * before today's tips are ready go to /home, not /todays-tips. The tips API (get_model
 * "predictions") returns the latest day it holds whatever date is asked for, so before the
 * new day's tips are generated /todays-tips shows yesterday's tips.
 *
 * The check downloads the day's tips (~1 MB, ~2.5 s), so it only runs in the morning
 * (owner, 3 Nov 2026: "morning-only check"). Race Day tips are out by about 9:45 am AEDT;
 * from midday the destination is kept with no wait.
 */

const HOME = "/home";
const TODAYS_TIPS = "/todays-tips";

/** The check runs before this hour (device clock, the same clock as the tips pages' "today"). */
export const CHECK_BEFORE_HOUR = 12;
const DEFAULT_TIMEOUT_MS = 6000;

/** The same day key the tips pages use ("03-11-2026"). */
export const tipsDateKey = (now = new Date()) => format(now, "dd-MM-yyyy");

/** True in the morning, when today's tips may not be out yet. */
export const tipsMayNotBeOut = (now = new Date()) => now.getHours() < CHECK_BEFORE_HOUR;

/** True when the tips data holds at least one race dated `date` with tips in it. */
export function hasTipsForDate(data, date) {
  if (!data || typeof data !== "object") return false;
  return Object.values(data).some(
    (race) =>
      race?.date === date &&
      Array.isArray(race.firstFourData) &&
      race.firstFourData.length > 0,
  );
}

const isTodaysTipsPath = (path) => {
  try {
    return new URL(path, "https://lexi.invalid").pathname === TODAYS_TIPS;
  } catch {
    return false;
  }
};

/**
 * `path` unchanged, or "/home" when `path` is /todays-tips (any query), it is morning and
 * today's tips are not out yet. If the check fails or takes longer than `timeoutMs`, `path`
 * is kept so signing in is never blocked.
 */
export async function landingAfterSignIn(
  path = TODAYS_TIPS,
  {
    now = new Date(),
    timeoutMs = DEFAULT_TIMEOUT_MS,
    fetchTips = fetchLeaderboardData,
  } = {},
) {
  if (!isTodaysTipsPath(path) || !tipsMayNotBeOut(now)) return path;
  const date = tipsDateKey(now);
  let timer;
  try {
    const response = await Promise.race([
      fetchTips({ date, collection: "predictions" }),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("tips check timed out")), timeoutMs);
      }),
    ]);
    return hasTipsForDate(response?.data, date) ? path : HOME;
  } catch {
    return path;
  } finally {
    clearTimeout(timer);
  }
}
